import React, { useState, useEffect, useRef } from 'react';
import { Download, FileText, Image as ImageIcon, File, Loader2, X, AlertCircle } from 'lucide-react';
import { useSermonMedia } from '../hooks/useSermonMedia';

interface MediaDownloaderProps {
  sermonId: string;
  sectionId: string;
  onClose: () => void;
}

interface MediaFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  signedUrl?: string;
}

export function MediaDownloader({ sermonId, sectionId, onClose }: MediaDownloaderProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const { loadSectionMedia, getSignedUrl, downloadFile } = useSermonMedia(sermonId);

  useEffect(() => {
    const loadMedia = async () => {
      setLoading(true);
      try {
        const sectionMedia = await loadSectionMedia(sermonId, sectionId);
        const mediaWithUrls = await Promise.all(
          sectionMedia.map(async (media) => ({
            ...media,
            signedUrl: await getSignedUrl(media.file_path)
          }))
        );
        setFiles(mediaWithUrls);
      } catch (error) {
        console.error('Error loading media:', error);
        setError('Failed to load media files. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadMedia();
  }, [sermonId, sectionId, loadSectionMedia, getSignedUrl]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-electric" />;
    if (fileType.includes('pdf')) return <FileText className="h-5 w-5 text-crimson" />;
    if (fileType.includes('word') || fileType.includes('doc')) return <FileText className="h-5 w-5 text-electric" />;
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return <FileText className="h-5 w-5 text-gold" />;
    return <File className="h-5 w-5 text-slate" />;
  };

  const handleDownload = async (file: MediaFile) => {
    try {
      setDownloadingFile(file.id);
      setError(null);
      
      // Use the downloadFile function from the hook for better retry logic
      await downloadFile(file.file_path, file.file_name);
      
      return true;
    } catch (err) {
      console.error('Download error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to download file. Please try again.';
      setError(errorMessage);
      return false;
    } finally {
      setDownloadingFile(null);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-midnight">Download Media Files</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate hover:text-midnight rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-electric" />
          </div>
        ) : error ? (
          <div className="flex items-center text-crimson bg-crimson/10 p-4 rounded-lg">
            <AlertCircle className="h-6 w-6 mr-2" />
            <p>{error}</p>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate">No media files available for this section.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {files.map((file) => (
              <div key={file.id} className="flex items-center justify-between p-3 bg-sand/10 rounded-lg">
                <div className="flex items-center overflow-hidden">
                  {getFileIcon(file.file_type)}
                  <span className="ml-2 text-sm text-midnight truncate" title={file.file_name}>
                    {file.file_name}
                  </span>
                  <span className="ml-2 text-xs text-slate">
                    {formatFileSize(file.file_size)}
                  </span>
                </div>
                <button
                  onClick={() => handleDownload(file)}
                  disabled={downloadingFile === file.id}
                  className="p-1 text-slate hover:text-electric rounded disabled:opacity-50"
                  title="Download"
                >
                  {downloadingFile === file.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-sand/20 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate hover:text-midnight"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return modalContent;
}
